import * as S from './BillingHistory.styled';

export interface BillingHistoryItem {
    id: string; // Stripe charge ID
    created: string; // ISO timestamp
    description: string;
    amount: number;
    currency: string;
    status: 'successful' | 'failed' | 'pending';
    status_display: string;
    invoice_number?: string | null;
    payment_method_details?: {
        type: string | null;
        last4: string | null;
        brand: string | null;
    };
    failure_message?: string | null;
    failure_code?: string | null;
    receipt_url?: string | null;
    paid: boolean;
    refunded: boolean;
    amount_refunded: number;
}

interface BillingHistoryProps {
    history: BillingHistoryItem[];
    onDownloadReceipt: (url: string) => void;
}

export default function BillingHistory({ history, onDownloadReceipt }: BillingHistoryProps) {
    const renderStatusBadge = (status: string, statusDisplay: string) => {
        switch (status) {
            case 'successful':
                return <S.PaidBadge>{statusDisplay}</S.PaidBadge>;
            case 'failed':
                return <S.FailedBadge>{statusDisplay}</S.FailedBadge>;
            case 'pending':
                return <S.PendingBadge>{statusDisplay}</S.PendingBadge>;
            default:
                return <S.PaidBadge>{statusDisplay}</S.PaidBadge>;
        }
    };

    const formatDate = (isoDate: string) => {
        return new Date(isoDate).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const renderPaymentMethod = (details?: {
        type: string | null;
        last4: string | null;
        brand: string | null;
    }) => {
        if (!details || !details.type) return '-';

        if (details.type === 'card' && details.brand && details.last4) {
            return `${details.brand} ••••${details.last4}`;
        }

        return details.type;
    };

    return (
        <div>
            <S.SectionTitle>Billing History</S.SectionTitle>

            {history.length === 0 ? (
                <S.TableWrapper>
                    <S.EmptyState>No billing history available</S.EmptyState>
                </S.TableWrapper>
            ) : (
                <S.TableWrapper>
                    <S.Table>
                        <thead>
                            <tr>
                                <S.TableHeader>Date</S.TableHeader>
                                <S.TableHeader>Description</S.TableHeader>
                                <S.TableHeader>Payment Method</S.TableHeader>
                                <S.TableHeader>Amount</S.TableHeader>
                                <S.TableHeader>Status</S.TableHeader>
                                <S.TableHeader>Receipt</S.TableHeader>
                            </tr>
                        </thead>
                        <tbody>
                            {history.map((item) => (
                                <S.TableRow key={item.id}>
                                    <S.TableCell>{formatDate(item.created)}</S.TableCell>
                                    <S.TableCell>
                                        <div>{item.description}</div>
                                        {item.invoice_number && (
                                            <div style={{ fontSize: '0.85em', color: '#666' }}>
                                                Invoice: {item.invoice_number}
                                            </div>
                                        )}
                                        {item.failure_message && (
                                            <div
                                                style={{
                                                    fontSize: '0.85em',
                                                    color: '#d32f2f',
                                                    marginTop: '4px',
                                                }}
                                            >
                                                {item.failure_message}
                                            </div>
                                        )}
                                        {item.refunded && (
                                            <div
                                                style={{
                                                    fontSize: '0.85em',
                                                    color: '#f57c00',
                                                    marginTop: '4px',
                                                }}
                                            >
                                                Refunded: ${item.amount_refunded.toFixed(2)}
                                            </div>
                                        )}
                                    </S.TableCell>
                                    <S.TableCell>
                                        {renderPaymentMethod(item.payment_method_details)}
                                    </S.TableCell>
                                    <S.TableCell>
                                        ${item.amount.toFixed(2)} {item.currency.toUpperCase()}
                                    </S.TableCell>
                                    <S.TableCell>
                                        {renderStatusBadge(item.status, item.status_display)}
                                    </S.TableCell>
                                    <S.TableCell>
                                        {item.receipt_url ? (
                                            <S.DownloadLink
                                                onClick={() =>
                                                    item.receipt_url &&
                                                    onDownloadReceipt(item.receipt_url)
                                                }
                                            >
                                                <S.DownloadIconStyled />
                                                Download
                                            </S.DownloadLink>
                                        ) : (
                                            <S.DownloadLink disabled>-</S.DownloadLink>
                                        )}
                                    </S.TableCell>
                                </S.TableRow>
                            ))}
                        </tbody>
                    </S.Table>
                </S.TableWrapper>
            )}
        </div>
    );
}
